import { NavItem, NavListItemWithBlockDescription, Select, SelectProps } from '@billy/design-system'
import omit from 'lodash/omit'
import { forwardRef, ReactElement, Ref, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchAccounts } from '@features/accounts/hooks/useFetchAccounts'
import { Account } from '@modules-deprecated/app/accounts/types'

import { useFetchBankAccounts } from '../../hooks/useFetchBankAccounts'
import { BankAccount } from '../../types/bankAccount'
import { BankAccountValue } from './types/bankAccountValue'
import { getItems } from './utils/getItems'

export interface BankAccountSelectProps
  extends Omit<SelectProps<BankAccountValue>, 'items' | 'dropdownItemRender' | 'dropdownFetching' | 'onSelect'> {
  onSelect?: (id?: string, value?: Account) => void
  excludedAccounts?: string[]
  accounts?: Account[]
  bankAccounts?: BankAccount[]
}

export const BankAccountSelect = forwardRef(
  (
    { onSelect, excludedAccounts, accounts, bankAccounts, ...selectProps }: BankAccountSelectProps,
    ref: Ref<HTMLDivElement | null>,
  ): ReactElement => {
    const { t } = useTranslation()
    const { bankAccounts: fetchedBankAccounts, isLoading: isLoadingBankAccounts } = useFetchBankAccounts({
      enabled: !bankAccounts,
    })
    const { accounts: fetchedAccounts, isLoading: isLoadingBookkeepingAccounts } = useFetchAccounts({
      enabled: !accounts,
    })
    const bookkeepingAccounts = accounts ?? fetchedAccounts
    const resolvedBankAccounts = bankAccounts ?? fetchedBankAccounts

    const isLoading = (!accounts && isLoadingBookkeepingAccounts) || (!bankAccounts && isLoadingBankAccounts)

    const activeBookkeepingAccounts = useMemo(() => {
      return bookkeepingAccounts?.filter(
        (account) =>
          account.isBankAccount &&
          !account.isArchived &&
          (excludedAccounts && excludedAccounts.length > 0 ? !excludedAccounts.includes(account.id) : true),
      )
    }, [bookkeepingAccounts, excludedAccounts])

    const items = useMemo(
      () => getItems(activeBookkeepingAccounts || [], resolvedBankAccounts, t),
      [activeBookkeepingAccounts, resolvedBankAccounts, t],
    )

    const getDisplayValue = useCallback((item: NavItem<BankAccountValue>) => `${item.value.displayData.title}`, [])

    const handleSelect = useCallback(
      (id?: string, value?: BankAccountValue) => {
        const valueFiltered = omit(value, 'displayData')
        onSelect?.(id, valueFiltered)
      },
      [onSelect],
    )

    return (
      <Select
        {...selectProps}
        dropdownFetching={isLoading}
        dropdownItemRender={(props) => <NavListItemWithBlockDescription {...props} />}
        getDisplayValue={getDisplayValue}
        dropdownSize="fitTrigger"
        items={items}
        onSelect={handleSelect}
        ref={ref}
      />
    )
  },
)
